import React from 'react';
import PropTypes from 'prop-types';
// components
import WidgetButtons from './WidgetButtons';
import { ActionCards, URGENCY_STATES } from '../actionCards/ActionCards';
// redux
import { useSelector } from '../../redux/store';
// hooks
import { useToggle } from '../../hooks';

// Tooltips for each widget
const WIDGET_TOOLTIPS = {
    upcoming: 'Consultations happening in the next 7 days. Prepare for these appointments.',
    past: 'Past appointments needing attendance to be recorded. Mark as attended or FTA.',
};

const BookedTableReportsWidget = ({ onWidgetClick, dateRangeProps, selectedWidget, widgetToggle }) => {
    const { toggle: internalToggle, onOpen, onClose } = useToggle(true);
    const toggle = widgetToggle !== undefined ? widgetToggle : internalToggle;
    const { widgetCounter, loadedKeys, expectedCount } = useSelector((state) => state.widgetCounter);

    const handleOpen = () => onOpen();
    const handleClose = () => onClose();

    // Widget configurations
    const cardConfigs = [
        { key: 'upcoming', label: 'Upcoming this week', icon: 'mdi:calendar-clock', iconBgColor: '#dbeafe' },
        { key: 'past', label: 'Past, no outcome', icon: 'mdi:clipboard-alert', iconBgColor: '#fee2e2' },
    ];

    // Get count from widget counter
    const getCount = (key) => {
        const counter = widgetCounter?.find((c) => c.key === key);
        return counter?.count || 0;
    };

    // Determine urgency state based on count and widget type
    const getUrgencyState = (key, count) => {
        if (count === 0) return URGENCY_STATES.NEUTRAL;

        if (key === 'past') {
            // Past appointments without outcome are urgent
            if (count > 10) return URGENCY_STATES.URGENT;
            if (count > 0) return URGENCY_STATES.WARNING;
        }
        if (key === 'upcoming') {
            // Upcoming is informational, good to have
            return count > 0 ? URGENCY_STATES.GOOD : URGENCY_STATES.NEUTRAL;
        }

        return URGENCY_STATES.NEUTRAL;
    };

    // Generate badges for each widget
    const generateBadges = (key, count) => {
        const badges = [];

        if (key === 'upcoming' && count > 0) {
            badges.push({ type: 'on-track', text: 'Prep needed', showDot: true, dotColor: '#3b82f6' });
        }
        if (key === 'past' && count > 0) {
            badges.push({ type: 'overdue', text: 'Record outcome', showDot: true, dotColor: '#ef4444' });
        }
        if (count === 0 && key === 'past') {
            badges.push({ type: 'on-track', text: 'All recorded', showDot: true, dotColor: '#10b981' });
        }

        return badges;
    };

    // Build cards data for ActionCards component
    const cards = cardConfigs.map((config) => {
        const count = getCount(config.key);
        return {
            key: config.key,
            count,
            label: config.label,
            icon: config.icon,
            iconBgColor: config.iconBgColor,
            urgencyState: getUrgencyState(config.key, count),
            badges: generateBadges(config.key, count),
            tooltip: WIDGET_TOOLTIPS[config.key],
        };
    });

    // Handle widget click - filter table based on widget type
    const handleCardClick = (card) => {
        // Pass the widget key to parent for filtering
        onWidgetClick(card.key);
    };

    return (
        <>
            {dateRangeProps && <WidgetButtons
                toggle={toggle}
                dateRangeProps={dateRangeProps}
                handleOpen={handleOpen}
                handleClose={handleClose}
                type="ConsultantBooked"
            />}
            {toggle && (
                <ActionCards
                    cards={cards}
                    onCardClick={handleCardClick}
                    sortByUrgency={false}
                    selectedKey={selectedWidget}
                    loading={expectedCount > 0 && loadedKeys.length < expectedCount}
                    loadedKeys={loadedKeys}
                    skeletonCount={2}
                />
            )}
        </>
    );
};

export default BookedTableReportsWidget;

BookedTableReportsWidget.propTypes = {
    onWidgetClick: PropTypes.func,
    dateRangeProps: PropTypes.any,
    selectedWidget: PropTypes.string,
    widgetToggle: PropTypes.bool,
};
